import * as React from 'react';
import { connect } from 'react-redux';
import { find } from 'lodash';
import API from '../../redux/api';
import { replace, push, goBack } from 'react-router-redux';
import { getBooking, resolveBooking } from '../../redux/actions/bookings';
import { getCompanies } from '../../redux/actions/companies';

import {
  Card,
  Button,
  Icon,
  IconButton,
  Notification,
  BookingPayment,
  BookingPrices,
  HeadingSection,
  RouteWrapper,
  Section,
  Content,
  Box,
} from '../../components/index';

interface Props {
  match: any;
  companies: Array<any>;
  accessories: Array<any>;
  vehicleTypes: Array<any>;
  dispatch: Function;
}

interface State {
  accessories?: any;
  booking: any;
  company?: any;
  drivers?: any;
  error: any;
  id: number;
  isLoading: boolean;
  isReady: boolean;
  totalOutstanding?: number;
  vehicleType?: any;
  formError: boolean | string;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: this.props.match.params.id,
      isReady: false,
      error: false,
      isLoading: false,
      booking: false,
      totalOutstanding: 0,
      formError: false,
    };
  }

  /**
   * Load booking and relations from API
   */
  async componentDidMount() {
    const response = await this.props.dispatch(getBooking(this.state.id));
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }

    // Throw an error if we can't actually resolve this
    if (
      response.data.status !== 'ON-HOLD' ||
      !response.data.is_final_form_complete
    ) {
      this.setState({ error: 'This booking cannot be resolved' });
      return;
    }

    // Retrieve attached resources
    await this.props.dispatch(getCompanies());

    // Fetch the attached company, if any
    let company = undefined;
    if (response.data.company_id !== null) {
      company = find(this.props.companies, { id: response.data.company_id });
    }

    this.setState({
      company,
      isReady: true,
      booking: response.data,
      drivers: response.data.drivers,
      accessories: response.data.accessories,
      totalOutstanding: Number(
        (response.data.total_cost - response.data.total_paid).toFixed(2),
      ),
      vehicleType: find(this.props.vehicleTypes, {
        id: response.data.vehicle_type_id,
      }),
    });
  }

  /**
   * Update the payment status using a response from the server
   * @param {Object} response JSON response
   */
  handleUpdate(response) {
    let updateState: any = {
      error: response.error,
    };
    if (!response.error) {
      updateState = {
        booking: response.data,
        error: response.error,
        id: response.data.id,
        isLoading: false,
        totalOutstanding: Number(
          (response.data.total_cost - response.data.total_paid).toFixed(2),
        ),
      };
    }
    this.setState(updateState, () => {
      this.props.dispatch(replace(`/bookings/${this.state.id}/resolve`));
    });
  }

  /**
   * Render the appropriate payment actions
   */
  renderActions() {
    const bookingPaid =
      this.state.booking.total_cost === this.state.booking.total_paid;
    return (
      <div>
        {bookingPaid && (
          <Notification type="success">
            <Icon icon="check-circle" /> This booking has been fully paid for
          </Notification>
        )}
        {this.state.booking.payment_status === 2 && (
          <Notification type="success">
            <Icon icon="check-circle" /> This booking will be paid for via an
            account payment
          </Notification>
        )}
        {!bookingPaid && this.state.booking.payment_status !== 2 && (
          <div className="field">
            <BookingPayment
              booking={this.state.booking}
              handleUpdate={(response) => {
                this.setState({ isLoading: true }, () => {
                  this.handleUpdate(response);
                });
              }}
            />
          </div>
        )}
        {(bookingPaid || this.state.booking.payment_status === 2) && (
          <div className="field">
            <div className="has-text-right">
              <IconButton
                icon="check-circle"
                className="is-medium"
                handleClick={async () => {
                  this.setState({ isLoading: true });
                  const response = await this.props.dispatch(
                    resolveBooking(this.state.id),
                  );
                  if (response.error) {
                    this.setState({
                      formError: response.error,
                      isLoading: false,
                    });
                    return;
                  }
                  this.props.dispatch(push('/bookings/status/ON-HOLD'));
                  window.open(
                    `${API.getURL()}/bookings/${
                      this.state.id
                    }/pdf/hire-agreement?${API.authParams}`,
                  );
                }}
              >
                Mark as Complete
              </IconButton>
            </div>
          </div>
        )}
        {this.state.formError && (
          <Notification type="danger">{this.state.formError}</Notification>
        )}
      </div>
    );
  }

  render() {
    return (
      <RouteWrapper
        title="Resolve Payment"
        requireAuth
        requirePermission="MANAGE_BOOKINGS"
        breadcrumbs={[
          {
            link: `/bookings/${this.state.id}`,
            text: `Booking ${this.state.booking.invoiceId}`,
          },
          {
            link: `/bookings/${this.state.id}/resolve`,
            text: 'Resolve Payment',
          },
        ]}
        error={this.state.error}
        ready={this.state.isReady}
        loading={this.state.isLoading}
      >
        <HeadingSection
          title="Resolve Payment"
          controls={[
            <IconButton
              key="return"
              icon="undo"
              className="is-default is-pulled-right"
              handleClick={() => {
                this.props.dispatch(goBack());
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <Content>
            <Box>
              <BookingPrices
                booking={this.state.booking}
                vehicleType={this.state.vehicleType}
              />
            </Box>
            {this.renderActions()}
            <br />
            <div className="buttons is-right">
              <Button
                className="is-default"
                handleClick={async () => {
                  this.props.dispatch(push('/dashboard'));
                }}
              >
                Return to Dashboard
              </Button>
            </div>
          </Content>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ companies, accessories, vehicleTypes }) => ({
  companies: companies.companies,
  accessories: accessories.accessories,
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(Route);
